import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { PayPalScriptProvider } from '@paypal/react-paypal-js';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Feather, Check, PartyPopper } from 'lucide-react';
import { PricingIntro } from '@/components/PricingIntro';
import { PlanCard } from '@/components/PlanCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const writerPerks = (w) => [
  `${w.prompts} AI prompts every day`,
  `${w.illustrations} story illustrations a day`,
  'Sprint timer, word goals & streaks',
  'Unlimited stories, sharing & gallery',
  'Supporter badge on your profile',
];

const Pricing = () => {
  const { user, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [purchased, setPurchased] = useState(null);

  useEffect(() => {
    axios.get(`${API}/plans`).then(({ data }) => setConfig(data)).catch(() => setConfig({ plans: [], paypal: { configured: false }, free_limits: {}, writer_limits: {} }));
  }, []);

  const handleSuccess = async (data) => {
    setPurchased(data);
    toast.success('Welcome to the Writer plan!');
    await checkAuth();
  };

  const paypalReady = Boolean(config?.paypal?.configured);
  const expires = purchased?.plan_expires_at ? new Date(purchased.plan_expires_at).toLocaleDateString(undefined, { dateStyle: 'long' }) : null;

  const content = (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-rose-50 relative overflow-hidden" data-testid="pricing-page">
      <div className="absolute top-0 -left-32 w-96 h-96 bg-orange-200 rounded-full opacity-40 blur-3xl" aria-hidden="true" />
      <div className="absolute bottom-0 -right-32 w-96 h-96 bg-rose-200 rounded-full opacity-40 blur-3xl" aria-hidden="true" />

      <nav className="relative z-10 container mx-auto px-6 py-6 flex items-center justify-between">
        <Link to="/" className="flex items-center space-x-2">
          <div className="bg-white/60 backdrop-blur-sm p-2 rounded-full shadow-sm"><Feather className="h-5 w-5 text-amber-900" /></div>
          <span className="font-display text-xl font-bold text-amber-950">Flash Fiction</span>
        </Link>
        <div className="flex items-center space-x-3">
          <Link to="/gallery"><Button variant="ghost" className="text-amber-900 hover:bg-amber-100">Gallery</Button></Link>
          <Link to="/about"><Button variant="ghost" className="text-amber-900 hover:bg-amber-100 hidden sm:inline-flex" data-testid="pricing-nav-about">About</Button></Link>
          {user ? (
            <Link to="/dashboard"><Button className="bg-amber-900 hover:bg-amber-800 text-amber-50" data-testid="pricing-nav-dashboard">Dashboard</Button></Link>
          ) : (
            <Link to="/login"><Button variant="ghost" className="text-amber-900 hover:bg-amber-100" data-testid="pricing-nav-login">Sign In</Button></Link>
          )}
        </div>
      </nav>

      <main className="relative z-10 container mx-auto px-6 pb-24 space-y-20">
        <PricingIntro />

        {purchased ? (
          <section className="max-w-xl mx-auto bg-white/80 backdrop-blur border border-emerald-200 rounded-3xl p-10 text-center shadow-lg" data-testid="purchase-success">
            <PartyPopper className="h-10 w-10 text-emerald-600 mx-auto mb-4" />
            <h2 className="font-display text-3xl font-bold text-amber-950 mb-2">You&rsquo;re a Writer now</h2>
            <p className="text-amber-900/80 mb-6">Your {purchased.plan} plan is active{expires ? ` until ${expires}` : ''}. Time to write.</p>
            <Button onClick={() => navigate('/dashboard')} className="bg-amber-900 hover:bg-amber-800 text-amber-50 rounded-full px-8" data-testid="purchase-go-dashboard">Go to my studio</Button>
          </section>
        ) : (
          <section id="plans" className="scroll-mt-8" data-testid="pricing-plans">
            <div className="max-w-3xl mb-10">
              <p className="text-amber-800 text-sm font-medium tracking-wider uppercase mb-3">Simple pricing</p>
              <h2 className="font-display text-3xl md:text-4xl font-bold text-amber-950">Pay once. Write for a season.</h2>
              <p className="text-amber-900/75 mt-3">Try it free for a week. Then buy a block of time — no subscriptions that quietly renew. When it runs out, decide again; your stories and writing tools stay yours.</p>
            </div>
            {config && (
              <div className="grid md:grid-cols-4 gap-6 items-stretch">
                <div className="rounded-3xl p-7 border border-dashed border-amber-300 bg-amber-50/60 flex flex-col" data-testid="plan-card-free">
                  <p className="text-sm font-medium tracking-wider uppercase text-amber-700">{config.trial_days}-day free trial</p>
                  <div className="mt-3"><span className="font-display text-5xl font-black text-amber-950">$0</span></div>
                  <ul className="mt-6 space-y-2 text-sm text-amber-900 flex-1">
                    {[`${config.free_limits.prompts} AI prompts a day for ${config.trial_days} days`, 'Prompt collections & Prompt of the Day', 'Editor, sprint timer & word goals', 'Library, sharing & gallery — yours to keep'].map((p) => (
                      <li key={p} className="flex gap-2"><Check className="h-4 w-4 mt-0.5 text-amber-600 shrink-0" /><span>{p}</span></li>
                    ))}
                  </ul>
                  <div className="mt-7">
                    <Link to={user ? '/dashboard' : '/register'}><Button variant="outline" className="w-full rounded-full border-amber-900 text-amber-900 hover:bg-amber-100" data-testid="plan-free-cta">{user ? 'Keep writing free' : 'Start free'}</Button></Link>
                  </div>
                </div>
                {config.plans.map((plan) => (
                  <PlanCard key={plan.id} plan={plan} user={user} paypalReady={paypalReady} featured={plan.id === 'year'} perks={writerPerks(config.writer_limits)} onSuccess={handleSuccess} />
                ))}
              </div>
            )}
            {config && !paypalReady && (
              <p className="mt-6 text-sm text-amber-800/70 text-center" data-testid="payments-pending-note">Paid plans open shortly — the free trial is fully available today.</p>
            )}
          </section>
        )}

        <section className="max-w-3xl mx-auto grid sm:grid-cols-2 gap-8 text-sm text-amber-900/80" data-testid="pricing-faq">
          <div><h3 className="font-display font-bold text-amber-950 mb-1">Does it auto‑renew?</h3><p>No. You buy a block of time. When it ends, AI prompts pause but your library, editor and sharing keep working — buy again whenever you like.</p></div>
          <div><h3 className="font-display font-bold text-amber-950 mb-1">What happens after the free week?</h3><p>The AI muse takes a break. Everything you wrote stays, and you can keep writing with your own ideas — or upgrade to bring the prompts back.</p></div>
          <div><h3 className="font-display font-bold text-amber-950 mb-1">Why daily limits?</h3><p>Prompts are generated by a live AI model. Limits keep the service sustainable and — honestly — three good prompts a day is all a writer needs.</p></div>
          <div><h3 className="font-display font-bold text-amber-950 mb-1">Can I buy time early?</h3><p>Yes. New purchases stack on top of your remaining time, so you never lose a day.</p></div>
          <div><h3 className="font-display font-bold text-amber-950 mb-1">How do I pay?</h3><p>Securely through PayPal — card or PayPal balance. We never see or store your card details.</p></div>
        </section>
      </main>
    </div>
  );

  if (paypalReady) {
    return (
      <PayPalScriptProvider options={{ clientId: config.paypal.client_id, currency: 'USD', intent: 'capture' }}>
        {content}
      </PayPalScriptProvider>
    );
  }
  return content;
};

export default Pricing;
